import { type WorkflowNodeType } from '@blaze-shared/api-contracts-domain';
import {
  type BaseSchema,
  type BlazeTableSchema,
  createElementSchemaId,
  type UrlParamsSchema,
} from '@blaze-shared/schema-domain';
import {
  type Connection,
  getParentLoopNode,
  MAIN_INPUT_NAME,
  type Workflow,
  type WorkflowNode,
} from '@blaze-shared/workflow-domain';

export const WORKFLOW_SCHEMA_KEY = Symbol('workflow');

export abstract class SchemaProvider<T extends WorkflowNode> {
  getSchemas(
    workflow: Workflow,
    node: T,
    schemas: {
      input: Map<string | symbol, BaseSchema>;
      output: Map<string | symbol, BaseSchema>;
      allSchemas: Record<string, BaseSchema>;
      userSchema?: BlazeTableSchema | null;
      urlSchema?: UrlParamsSchema | null;
      accountSchema?: BlazeTableSchema | null;
    },
    connections: Connection[],
  ): Map<string | symbol, BaseSchema> {
    const parentNode = workflow.nodes.find((n) => n.nextNodeId === node.id);
    const parentLoopNode = getParentLoopNode(workflow.nodes, workflow.startNodeId, node.id);
    const nodeSchemas = new Map<string | symbol, BaseSchema>();

    const workflowSchema = schemas.allSchemas[workflow.id];

    if (workflowSchema) {
      nodeSchemas.set(WORKFLOW_SCHEMA_KEY, workflowSchema);
    }

    const input1Schema = parentNode
      ? schemas.output.get(parentNode.id)
      : schemas.input.get(node.id);
    if (input1Schema) {
      nodeSchemas.set(MAIN_INPUT_NAME, input1Schema);
    }

    if (schemas.userSchema) {
      nodeSchemas.set('User', schemas.userSchema);
    }

    if (schemas.accountSchema) {
      nodeSchemas.set('Account', schemas.accountSchema);
    }
    if (schemas.urlSchema) {
      nodeSchemas.set('Url', schemas.urlSchema);
    }

    if (parentLoopNode && input1Schema) {
      nodeSchemas.set('currentRow', input1Schema);
    }

    for (const inputName in workflow.inputs) {
      if (workflow.startNodeId !== node.id && inputName === MAIN_INPUT_NAME) {
        continue;
      }

      const connectionId = workflow.inputs[inputName];
      const connection = connections.find(({ id }) => id === connectionId);
      if (!connection?.source?.id) {
        continue;
      }

      let key = connection.source.id;
      if (connection.source.layerType === 'Ui') {
        key = createElementSchemaId(connection.source.id, connection.eventType);
      }

      const schema = schemas.allSchemas[key || ''];
      if (schema) {
        nodeSchemas.set(inputName, schema);
      }
    }

    // Named schemas declared on the workflow model (e.g. function `inputParams`,
    // server/triggered input schemas) — not deduced from UI connections.
    for (const inputSchema of workflow.inputSchemas ?? []) {
      const inputName = inputSchema.inputName;
      if (!inputName) {
        continue;
      }
      if (workflow.startNodeId !== node.id && inputName === MAIN_INPUT_NAME) {
        continue;
      }
      if (nodeSchemas.has(inputName)) {
        continue;
      }

      const datasetSchema = inputSchema.datasetId
        ? schemas.allSchemas[inputSchema.datasetId]
        : undefined;
      nodeSchemas.set(inputName, datasetSchema ?? inputSchema);
    }

    return nodeSchemas;
  }
}

export type SchemaProviderConstructor<T extends WorkflowNode> = new () => SchemaProvider<T>;

export class SchemaProviderRegistry {
  private static readonly registry = new Map<WorkflowNodeType, SchemaProviderConstructor<any>>();

  static registerSchemaProvider<T extends WorkflowNode>(
    nodeType: WorkflowNodeType,
    schemaProviderClass: SchemaProviderConstructor<T>,
  ): void {
    SchemaProviderRegistry.registry.set(nodeType, schemaProviderClass);
  }

  static getSchemaProvider<T extends WorkflowNode>(
    nodeType: WorkflowNodeType,
  ): SchemaProviderConstructor<T> {
    const schemaProviderClass = SchemaProviderRegistry.registry.get(nodeType);

    if (!schemaProviderClass) {
      throw new Error(`No schema provider registered for ${nodeType}`);
    }

    return schemaProviderClass;
  }
}

export function WorkflowNodeSchemaProvider<T extends WorkflowNode>(
  nodeType: WorkflowNodeType,
): (schemaProviderClass: SchemaProviderConstructor<T>) => void {
  return (schemaProviderClass: SchemaProviderConstructor<T>): void => {
    SchemaProviderRegistry.registerSchemaProvider(nodeType, schemaProviderClass);
  };
}
